import { useState, useEffect } from 'react';
import { Loader2, AlertTriangle, CheckCircle2, XCircle, Settings } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';

function FlagRow({ flag }) {
  const isAlert = flag.alert;
  return (
    <div className="flex items-center justify-between py-1.5 border-b last:border-b-0 border-border/50">
      <div className="flex items-center gap-2 text-sm">
        {isAlert ? (
          <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
        ) : (
          <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0" />
        )}
        <span className="font-mono text-xs">{flag.name}</span>
        <Badge variant="outline" className="text-[10px] px-1 py-0">
          {flag.category}
        </Badge>
      </div>
      <Badge variant={isAlert ? 'destructive' : 'default'} className="text-xs">
        {flag.state}
      </Badge>
    </div>
  );
}

export function RuntimeConfigCard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    diagnosticsAPI.runtimeConfig()
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch((err) => { if (!cancelled) setError(err.message || 'Failed to fetch runtime config'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const alertCount = data ? data.flags.filter(f => f.alert).length : 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Settings className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-lg">Runtime Config</CardTitle>
          {data && alertCount > 0 && (
            <Badge variant="destructive" className="text-xs ml-auto">
              {alertCount} alert{alertCount !== 1 ? 's' : ''}
            </Badge>
          )}
          {data && alertCount === 0 && (
            <Badge variant="default" className="text-xs ml-auto">
              all OK
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading...
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-500">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </div>
        )}
        {data && (
          <div>
            {data.flags.map((flag) => (
              <FlagRow key={flag.name} flag={flag} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
